// src/components/drillholes/data_panel/DrillholesWorkspace.tsx

import { cn } from '@/lib/utils';
import { useState } from 'react';

export type DrillholeStatus = {
  hole_id: string;
  spatial: boolean;
  has_intervals: boolean;
};

type DrillholeSubView = 'holes' | 'analysis' | '3d';

const SUB_ITEMS: { id: DrillholeSubView; label: string }[] = [
  { id: 'holes',    label: 'Holes'    },
  { id: 'analysis', label: 'Analysis' },
  { id: '3d',       label: '3D view'  },
];

const PAGE_SIZE = 50;

// ── Pipeline strip ─────────────────────────────────────────────────────────────

function PipelineStrip({ holes }: { holes: DrillholeStatus[] }) {
  const referenced = holes.length;
  const spatial    = holes.filter(h => h.spatial).length;
  const intervals  = holes.filter(h => h.has_intervals).length;
  const complete   = holes.filter(h => h.spatial && h.has_intervals).length;

  const steps = [
    { label: 'Referenced', value: referenced, sub: 'total holes'      },
    { label: 'Spatial',    value: spatial,    sub: 'collar + survey'   },
    { label: 'Intervals',  value: intervals,  sub: 'with log data'     },
    { label: 'Complete',   value: complete,   sub: 'fully ready',  highlight: true },
  ];

  return (
    <div className="grid grid-cols-4 border-b border-border">
      {steps.map((s, i) => (
        <div
          key={s.label}
          className={cn(
            'flex flex-col gap-1 px-4 py-3',
            i < steps.length - 1 && 'border-r border-border',
            s.highlight && 'bg-muted/30'
          )}
        >
          <span className="text-[10px] text-muted-foreground">{s.label}</span>
          <span className={cn(
            'text-xl font-medium leading-none',
            s.highlight ? 'text-emerald-500' : 'text-foreground'
          )}>
            {s.value.toLocaleString()}
          </span>
          <span className="text-[10px] text-muted-foreground/60">{s.sub}</span>
        </div>
      ))}
    </div>
  );
}

// ── Status dot ─────────────────────────────────────────────────────────────────

function Dot({ on }: { on: boolean }) {
  return (
    <span className={cn(
      'inline-block w-2 h-2 rounded-full',
      on ? 'bg-emerald-500' : 'bg-border'
    )} />
  );
}

// ── Table ──────────────────────────────────────────────────────────────────────

function HolesTable({ holes }: { holes: DrillholeStatus[] }) {
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(0);

  const q        = search.trim().toLowerCase();
  const filtered = q ? holes.filter(h => h.hole_id.toLowerCase().includes(q)) : holes;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);
  const pageRows   = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(0); };

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border flex-shrink-0">
        <div className="relative flex-1 max-w-[220px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="5" cy="5" r="3.5"/><line x1="7.5" y1="7.5" x2="10.5" y2="10.5"/>
          </svg>
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Filter by hole ID…"
            className="w-full h-7 pl-7 pr-2 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <span className="text-xs text-muted-foreground ml-1">
          {filtered.length.toLocaleString()} hole{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
        <table className="w-full border-collapse text-[11px]" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-left text-[10px] font-medium text-muted-foreground">Hole ID</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-center text-[10px] font-medium text-muted-foreground w-[80px]">Collar</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-center text-[10px] font-medium text-muted-foreground w-[80px]">Survey</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-center text-[10px] font-medium text-muted-foreground w-[80px]">Intervals</th>
              <th className="sticky top-0 bg-muted border-b border-border px-3 py-2 text-center text-[10px] font-medium text-muted-foreground w-[80px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((h) => {
              const complete = h.spatial && h.has_intervals;
              return (
                <tr key={h.hole_id} className="group hover:bg-muted/40">
                  <td className="px-3 py-1.5 border-b border-border font-mono text-foreground">{h.hole_id}</td>
                  <td className="px-3 py-1.5 border-b border-border text-center"><Dot on={h.spatial} /></td>
                  <td className="px-3 py-1.5 border-b border-border text-center"><Dot on={h.spatial} /></td>
                  <td className="px-3 py-1.5 border-b border-border text-center"><Dot on={h.has_intervals} /></td>
                  <td className="px-3 py-1.5 border-b border-border text-center">
                    <span className={cn(
                      'inline-block text-[10px] px-2 py-0.5 rounded-full',
                      complete
                        ? 'bg-emerald-500/10 text-emerald-500'
                        : 'bg-muted text-muted-foreground/60'
                    )}>
                      {complete ? 'ready' : 'partial'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-border flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length.toLocaleString()}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(0)} disabled={safePage === 0} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">‹</button>
          <span className="px-2 text-xs text-foreground">{safePage + 1} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage === totalPages - 1} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={safePage === totalPages - 1} className="px-1.5 py-1 text-xs rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed text-muted-foreground">»</button>
        </div>
      </div>

    </div>
  );
}

// ── Placeholder views ──────────────────────────────────────────────────────────

function AnalysisView() {
  return (
    <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
      Analysis — coming soon
    </div>
  );
}

function ThreeDView() {
  return (
    <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
      3D viewer — coming soon
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

interface Props {
  holes:   DrillholeStatus[];
  loading: boolean;
}

export function DrillholesWorkspace({ holes, loading }: Props) {
  const [subView, setSubView] = useState<DrillholeSubView>('holes');

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-border border-t-blue-500 animate-spin" />
      </div>
    );
  }

  if (holes.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <p className="text-sm font-medium text-muted-foreground">No drillholes yet</p>
          <p className="mt-1 text-xs text-muted-foreground/60">Upload collars and surveys to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0">

      {/* Sub-sidebar — desktop only */}
      <div className="hidden md:flex w-[220px] flex-shrink-0 px-3 pt-6 flex-col gap-1">
        {SUB_ITEMS.map((item) => {
          const active = subView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setSubView(item.id)}
              className={cn(
                'flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-sm transition-colors text-left',
                active ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <span className={cn(
                'w-2 h-2 rounded-sm rotate-45 flex-shrink-0 transition-colors',
                active ? 'bg-blue-500' : 'opacity-0'
              )} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Main panel */}
      <div className="flex flex-col flex-1 min-h-0 min-w-0">
        {subView === 'holes' && (
          <>
            <PipelineStrip holes={holes} />
            <HolesTable holes={holes} />
          </>
        )}
        {subView === 'analysis' && <AnalysisView />}
        {subView === '3d'       && <ThreeDView />}
      </div>

    </div>
  );
}